import { useEffect, useState } from 'react'
import styles from './find-your-car.module.css'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import {
  capitalize,
  normalizeVrm,
  splitVariantDescription,
} from './find-your-car.utils'
import { useMaintenanceContext } from '../../context/maintenance-context'
import {
  FindByRegistration,
  type FindByRegistrationProps,
} from './partials/find-by-registration/find-by-registration'
import { LoadingSpinner } from '@cinch-nx/shared-ui'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import { Alert, Text } from '@cinch-labs/design-system'

export const FindYourCar: React.FC = () => {
  const {
    dispatch,
    vehicleMake,
    vehicleModel,
    vrm: selectedVrm,
    searchedVrm = '',
    step,
    mileage,
    canBookAppointment,
  } = useMaintenanceContext()

  const { trackEvent } = useAnalytics()

  const [vrm, setVrm] = useState(searchedVrm)
  const [hasUserTyped, setHasUserTyped] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [chooseCarAlert, setChooseCarAlert] = useState(false)

  useEffect(() => {
    if (!hasUserTyped) {
      setVrm(searchedVrm || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetRegistrationState = () => {
    setChooseCarAlert(false)
    setError('')
    dispatch({ type: 'CLEAR_FOUND_VARIANTS' })
    dispatch({ type: 'SET_SELECTED_FOUND_VARIANT_ID', id: null })
    dispatch({
      type: 'SET_VEHICLE',
      make: '',
      model: '',
      vrm: '',
      vin: '',
      fuelType: '',
      mileage: 0,
    })
  }

  const findByRegistrationProps: FindByRegistrationProps = {
    vrm,
    setVrm: (v) => {
      setVrm(v)
      setHasUserTyped(true)
    },
    error,
    loading,
    setError,
    setLoading,
    vrmForFoundVariants: normalizeVrm(searchedVrm),
    onSelectFoundVariant: (variant) => {
      const cleanVrm = normalizeVrm(vrm)

      dispatch({
        type: 'SET_VEHICLE',
        make: capitalize(variant.make),
        model: capitalize(splitVariantDescription(variant.description).model),
        vrm: cleanVrm,
        mileage: mileage || 0,
        vin: variant.vin,
        fuelType: variant.fuelType,
      })

      dispatch({
        type: 'SET_SELECTED_FOUND_VARIANT_ID',
        id: variant.id,
      })
      setChooseCarAlert(false)
      setHasUserTyped(false)
    },
    onRegistrationSearch: () => {
      const registrationSearchEvent = {
        eventName: `Select car`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: 'cinch_servicing',
            action: 'car_search_by_reg',
            label: 'make_a_booking',
          },
        },
      }
      trackEvent(registrationSearchEvent)
      resetRegistrationState()
      setHasUserTyped(false)
    },
  }

  const handleContinue = () => {
    if (chooseCarAlert) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const hasSelectedCar = vehicleMake && vehicleModel && selectedVrm

    if (hasSelectedCar) {
      setChooseCarAlert(false)
      dispatch({ type: 'NEXT_STEP' })
      datadogRum.addAction('Cinch Servicing: Car Selected', {
        cinchServicing: true,
        step,
        page: window.location.pathname,
        vehicleMake: capitalize(vehicleMake),
        vehicleModel: capitalize(vehicleModel),
        vrm: normalizeVrm(selectedVrm),
        canBookAppointment,
      })
    } else {
      setChooseCarAlert(true)
    }
  }

  return (
    <CarMaintenanceStepWrapper
      title="Find your car"
      description="Enter your car registration."
      showContinueButton={!!vehicleMake && !!vehicleModel && !!selectedVrm}
      onContinue={handleContinue}
    >
      {chooseCarAlert && (
        <Alert
          type="error"
          data-testid="Alert"
          className={styles.alertWithMargin}
        >
          <Text element="span" fontSizeFixed="xs">
            Please select your car by searching using your registration.
          </Text>
        </Alert>
      )}

      <FindByRegistration {...findByRegistrationProps} />
      {loading && (
        <span role="status" style={{ marginBottom: 32 }}>
          <LoadingSpinner size={50} />
        </span>
      )}
    </CarMaintenanceStepWrapper>
  )
}
